import { createReducer, on } from '@ngrx/store';

import {
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  INITIAL_LOCATION_UPLOAD_STATE,
} from '@ps/survey-utils';

import {
  DEFAULT_CHILD_AUDIENCE_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
} from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
  SurveyCreationPreDraftActions,
} from './survey-creation-audience.actions';
import { hasLocaleChanged } from './utils/locale-changes.helper';
import {
  buildPreDraftChildren,
  filterMetadataForChild,
  patchChildren,
  resolveActiveChildKey,
} from './utils/audience-children.helper';
import {
  buildMetadataFormStateFromSurvey,
  buildQualificationsFormStateFromSurvey,
  buildQuotasFormStateFromSurvey,
  buildSuppliersMetaDataFromSurvey,
} from './domain/build-audience-form-state-from-survey';

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  on(
    SurveyCreationActions.clearSurveyId,
    (state): SurveyCreationAudienceState =>
      patchChildren(state, { surveyId: null, error: null }),
  ),

  on(
    SurveyCreationActions.init,
    (state, { surveyId }): SurveyCreationAudienceState => {
      // Idempotence guard: re-initialising the survey already in memory keeps
      // it rather than forcing a re-fetch. Navigations into the edit page now
      // reset first, so in practice this only absorbs a repeated init.
      const alreadyHydrated =
        !!surveyId &&
        state.parentId === surveyId &&
        !!state.children[surveyId]?.survey;

      if (alreadyHydrated) {
        return state;
      }

      const key = surveyId ?? DEFAULT_SURVEY_CHILD_KEY;
      return {
        parentId: surveyId,
        activeChildId: surveyId,
        children: {
          [key]: { ...DEFAULT_CHILD_AUDIENCE_STATE, key, surveyId },
        },
      };
    },
  ),

  // Pre-draft only: before a surveyId exists, each locale gets its own
  // child. Once parentId is set, the survey is real and its locale is fixed.
  on(
    SurveyCreationPreDraftActions.buildPreDraftChildren,
    (state, { localeData }): SurveyCreationAudienceState =>
      state.parentId
        ? state
        : {
            ...state,
            children: buildPreDraftChildren(state.children, localeData),
          },
  ),

  on(
    SurveyCreationPreDraftActions.selectAllPreDraftChildren,
    (state): SurveyCreationAudienceState =>
      patchChildren(state, { selected: true }),
  ),

  on(
    SurveyCreationPreDraftActions.clearAllPreDraftChildren,
    (state): SurveyCreationAudienceState =>
      patchChildren(state, { selected: false }),
  ),

  on(
    SurveyCreationPreDraftActions.selectPreDraftChildren,
    (state, { ids }): SurveyCreationAudienceState =>
      patchChildren(state, (child) => ({
        selected: ids.includes(child.key),
      })),
  ),

  // Hydrate the in-memory survey from the API response.
  on(
    SurveyCreationApiActions.loadSurveyForEditSuccess,
    (state, { survey }): SurveyCreationAudienceState =>
      patchChildren(state, {
        survey,
        advancedFeasibilityFilters: survey.advancedFeasibilityFilters,
        metadata: buildMetadataFormStateFromSurvey(survey),
        qualifications: buildQualificationsFormStateFromSurvey(survey),
        quotas: buildQuotasFormStateFromSurvey(survey),
        suppliersMetaData: buildSuppliersMetaDataFromSurvey(survey),
        error: null,
        selected: null,
      }),
  ),

  on(
    SurveyCreationActions.updateMetadata,
    (state, { metadata }): SurveyCreationAudienceState => {
      const isMultiLocale = Object.keys(state.children).length > 1;

      return patchChildren(state, (child) => {
        const childMetadata = isMultiLocale
          ? filterMetadataForChild(metadata, child)
          : metadata;
        return {
          metadata: childMetadata,
          advancedFeasibilityFilters: hasLocaleChanged(
            child.metadata,
            childMetadata,
          )
            ? { ...EMPTY_ADVANCED_FEASIBILITY_FILTERS }
            : child.advancedFeasibilityFilters,
        };
      });
    },
  ),

  on(
    SurveyCreationActions.updateQualifications,
    (state, { qualifications }): SurveyCreationAudienceState =>
      patchChildren(state, { qualifications }),
  ),

  on(
    SurveyCreationActions.updateQuotas,
    (state, { quotas }): SurveyCreationAudienceState =>
      patchChildren(state, { quotas }),
  ),

  on(
    SurveyCreationActions.uploadLocationCodes,
    (state, { qualificationCode }): SurveyCreationAudienceState =>
      patchChildren(state, {
        locationFileUpload: {
          qualificationCode,
          loading: true,
          error: null,
          filePath: null,
          result: null,
        },
      }),
  ),

  on(
    SurveyCreationApiActions.uploadLocationCodesSuccess,
    (state, { qualificationCode, result }): SurveyCreationAudienceState =>
      patchChildren(state, {
        locationFileUpload: {
          qualificationCode,
          loading: false,
          error: null,
          filePath: result.filePath,
          result,
        },
      }),
  ),

  on(
    SurveyCreationApiActions.uploadLocationCodesFailure,
    (state, { qualificationCode, error }): SurveyCreationAudienceState =>
      patchChildren(state, {
        locationFileUpload: {
          qualificationCode,
          loading: false,
          error,
          filePath: null,
          result: null,
        },
      }),
  ),

  on(
    SurveyCreationActions.clearLocationCodes,
    (state, { qualificationCode }): SurveyCreationAudienceState =>
      patchChildren(state, (child) =>
        child.locationFileUpload.qualificationCode === qualificationCode
          ? { locationFileUpload: { ...INITIAL_LOCATION_UPLOAD_STATE } }
          : {},
      ),
  ),

  on(
    SurveyCreationActions.applyAdvancedFilters,
    (state, { filters }): SurveyCreationAudienceState =>
      patchChildren(state, {
        advancedFeasibilityFilters: filters,
        error: null,
      }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveySuccess,
    SurveyCreationApiActions.createSurveyFromTemplateSuccess,
    (state, { survey }): SurveyCreationAudienceState => {
      const oldChild = state.children[resolveActiveChildKey(state)];

      return {
        parentId: survey.id,
        activeChildId: survey.id,
        children: {
          [survey.id]: {
            ...oldChild,
            key: survey.id,
            surveyId: survey.id,
            survey,
            error: null,
          },
        },
      };
    },
  ),

  on(
    SurveyCreationApiActions.createStandardSurveyFailure,
    SurveyCreationApiActions.createSurveyFromTemplateFailure,
    (state, { error }): SurveyCreationAudienceState =>
      patchChildren(state, { error }),
  ),

  on(
    SurveyCreationApiActions.saveSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState =>
      patchChildren(state, (child) => ({
        surveyId: survey.id,
        survey,
        error: null,
        metadata: { ...child.metadata, isDirty: false },
        qualifications: { ...child.qualifications, isDirty: false },
        quotas: { ...child.quotas, isDirty: false },
      })),
  ),

  on(
    SurveyCreationApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationAudienceState =>
      patchChildren(state, { error }),
  ),

  // No cloneSurveySuccess handler: navigateToEditSurvey$ dispatches reset()
  // right after it, so anything written here would be wiped before it is read.

  on(
    SurveyCreationApiActions.cloneSurveyFailure,
    (state, { error }): SurveyCreationAudienceState =>
      patchChildren(state, { error }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditFailure,
    (state, { error }): SurveyCreationAudienceState =>
      patchChildren(state, { error }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyTagsSuccess,
    (state, { tagIds }): SurveyCreationAudienceState =>
      patchChildren(state, (child) => ({
        error: null,
        tagLinks: { ...child.tagLinks, linkedTagIds: tagIds },
      })),
  ),

  on(
    SurveyCreationApiActions.loadSurveyTagsFailure,
    (state): SurveyCreationAudienceState =>
      patchChildren(state, (child) => ({
        tagLinks: { ...child.tagLinks, linkedTagIds: [] },
      })),
  ),

  on(
    SurveyCreationApiActions.linkSurveyTagsSuccess,
    (state): SurveyCreationAudienceState =>
      patchChildren(state, (child) => ({
        error: null,
        tagLinks: { ...child.tagLinks, linkTagsError: null },
      })),
  ),

  on(
    SurveyCreationApiActions.linkSurveyTagsFailure,
    (state, { error }): SurveyCreationAudienceState =>
      patchChildren(state, (child) => ({
        tagLinks: { ...child.tagLinks, linkTagsError: error },
      })),
  ),

  on(
    SurveyCreationActions.setSuppliersMetaDataBlendId,
    (state, { blendId }): SurveyCreationAudienceState =>
      patchChildren(state, { suppliersMetaData: { blendId } }),
  ),

  on(
    SurveyCreationActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
